/**
 * JS-1090b: audit-grade performance aggregates over the outcome ledger
 * (GET /api/v1/trade-ideas/outcome-ledger/performance).
 *
 * DESIGN CHOICE (documented per the ticket's "your call"): this is exported
 * as a standalone `PerformancePage` component, but it is composed as a
 * SECTION inside `LedgerPage.tsx` at the single route
 * `/portal/trade-ideas/ledger` (no separate `/portal/trade-ideas/performance`
 * route) -- both the row-level ledger table and the aggregate performance
 * content are reachable from that one route via an internal tab switch. A
 * single route reads better here: a reader auditing the record wants both
 * "what happened" (rows) and "how did it add up" (aggregates) without a full
 * navigation, and the two views share the SAME authorization gate (403) and
 * the same page-level provenance conventions, so splitting them into two
 * routed pages would just duplicate the 403/empty-state boilerplate for no
 * real benefit.
 *
 * Population reconciliation (the "credibility feature," per the ticket) gets
 * its own visually-weighted section near the top -- see
 * `PopulationReconciliationTable` below -- not an afterthought footnote.
 */
import { Link } from "react-router-dom";

import type {
  CohortAggregate,
  DistributionBucket,
  DrawdownPoint,
  PerformanceAggregatesResponse,
  PnlStats,
  ReturnOnRiskStats,
} from "@/domains/trade-ideas/lib/contracts";
import { archetypeLabel, directionView } from "@/domains/trade-ideas/lib/screenerLabels";
import { LEDGER_TARGET_PARAM } from "@/domains/trade-ideas/lib/tickerContextLink";
import { Badge } from "@/shared/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { cn } from "@/shared/lib/utils";

// Reuse of EditionPage's `signedText` convention (kept local -- see
// EditionPage.tsx:934 for the precedent this mirrors: prepend a "+" for a
// positive number, pass through non-numeric/absent values as an em dash).
function signedText(value: string | null | undefined): string {
  if (value == null) return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  const sign = parsed > 0 ? "+" : "";
  return `${sign}${value}`;
}

// Sign coloring is the one place color alone is allowed to carry meaning
// (per the ticket) -- ALWAYS paired with the +/- sign from signedText above,
// never color alone. Mirrors EditionPage.tsx's inline three-way ternary
// convention (kind === "BULLISH" ? "text-gain" : ... ? "text-loss" : neutral).
function pnlToneClass(value: string | null | undefined): string {
  if (value == null) return "text-muted-foreground";
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return "text-muted-foreground";
  return parsed > 0 ? "text-gain" : "text-loss";
}

// Round 2 (adversarial review, HIGH FIX 1 -- percent-unit fidelity): the
// backend ships two GENUINELY DIFFERENT numeric conventions on this
// response, and conflating them under one "multiply by 100" formatter
// silently misrepresented one of them by 100x:
//   * `pnl.decided_trade_win_rate` (outcome_ledger_reads._pnl_stats) is a
//     FRACTION in [0, 1] (`wins / decided_n`) -- needs *100 for display.
//   * `return_on_risk.{mean,median,aggregate}_return_on_risk_pct`
//     (outcome_ledger_reads._return_on_risk_stats) is ALREADY computed as
//     `(pnl / max_loss) * Decimal(100)` server-side -- the value "40" IS
//     "40%"; multiplying it again would render "4000%" for what is really a
//     40% return.
// Two DISTINCT formatters, never one shared function, so a future field
// added to either family can't silently borrow the wrong scale.
//
// Precision: `formatAlreadyPercent` (return-on-risk) keeps ONE decimal
// place -- the review's stated example ("RoR 40 / -50 / 25 rendering
// '40.0%' etc") confirms one-decimal display for the already-percent
// family. `decided_trade_win_rate` (the ONLY fraction-scaled field this
// page renders) is called out separately below with its OWN two-decimal-
// place formatter, per the review's explicit decision to give it more
// precision than a generic percentage gets (a win rate is the headline
// "how often does this actually work" number -- worth the extra digit of
// precision a reader can act on, where the other percentages are
// contextual/secondary figures for which one decimal is plenty).
//
// `decided_trade_win_rate` specifically: a FRACTION in [0, 1] -- needs *100
// for display, at two decimal places (see the block comment above).
function formatWinRatePct(value: string | null | undefined): string {
  if (value == null) return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return `${(parsed * 100).toFixed(2)}%`;
}

// Return-on-risk's three fields arrive ALREADY on a 0-100 percent scale
// (see the block comment above) -- append "%" only, never re-multiply.
function formatAlreadyPercent(value: string | null | undefined): string {
  if (value == null) return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return `${parsed.toFixed(1)}%`;
}

function formatUsd(value: string | null | undefined): string {
  if (value == null) return "—";
  return `$${signedText(value)}`;
}

function humanizeDisclosureKey(key: string): string {
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function SectionCard({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("rounded-lg border border-border bg-card p-4", className)}>
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

// --------------------------------------------------------------------------- //
// Population reconciliation -- THE credibility feature. Given real visual
// weight: a labeled table, not a footnote.
// --------------------------------------------------------------------------- //

function PopulationReconciliationTable({ performance }: { performance: PerformanceAggregatesResponse }) {
  const population = performance.population;
  if (!population) {
    return (
      <SectionCard title="Population accounting">
        <p className="mt-2 text-sm text-muted-foreground">
          Population reconciliation is unavailable for this read.
        </p>
      </SectionCard>
    );
  }
  const rows: Array<{ label: string; value: number }> = [
    { label: "Total documents", value: population.total_documents },
    { label: "Resolved", value: population.resolved_count },
    { label: "Open", value: population.open_count },
    { label: "Flagged", value: population.flagged_count },
    { label: "Unknown status", value: population.unknown_status_count },
  ];
  return (
    <SectionCard title="Population accounting" className="border-2">
      <p className="mt-1 text-[12px] text-muted-foreground">
        Every ledger document is accounted for below -- none is silently dropped from any statistic on this page.
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-5">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{row.label}</dt>
            <dd className="font-mono text-lg tabular-nums text-foreground">{row.value}</dd>
          </div>
        ))}
      </dl>
      {population.unknown_status_count > 0 && (
        <p className="mt-3 text-[12px] text-warning">{population.unknown_status_note}</p>
      )}
    </SectionCard>
  );
}

// --------------------------------------------------------------------------- //
// Headline block
// --------------------------------------------------------------------------- //

// Round 2 (adversarial review, HIGH FIX 2 -- per-stat exclusions): every
// headline statistic renders its OWN n AND its OWN exclusion count/reasons
// inline -- zero excluded renders as "0 excluded" (visible, never omitted),
// never silently dropped from the caption. A test reconciles each rendered
// n + excluded.total against the relevant population.
function ExclusionCaption({
  n,
  excluded,
  populationLabel,
}: {
  n: number;
  excluded: { total: number; by_reason: Record<string, number> } | undefined;
  populationLabel: string;
}) {
  const total = excluded?.total ?? 0;
  const reasons = Object.entries(excluded?.by_reason ?? {});
  return (
    <div className="text-[11px] text-muted-foreground">
      n = {n} {populationLabel}, {total} excluded
      {reasons.length > 0 && (
        <span>
          {" "}
          ({reasons.map(([reason, count], index) => (
            <span key={reason}>
              {index > 0 ? ", " : ""}
              {count} {reason}
            </span>
          ))})
        </span>
      )}
    </div>
  );
}

function HeadlineBlock({ performance }: { performance: PerformanceAggregatesResponse }) {
  const pnl: PnlStats | undefined = performance.pnl;
  const returnOnRisk: ReturnOnRiskStats | undefined = performance.return_on_risk;
  return (
    <SectionCard title="Headline">
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Net P&amp;L</div>
          <div className={cn("font-mono text-xl tabular-nums", pnlToneClass(pnl?.total_pnl_usd))}>
            {formatUsd(pnl?.total_pnl_usd)}
          </div>
          <ExclusionCaption n={pnl?.n ?? 0} excluded={pnl?.excluded} populationLabel="resolved trades with usable P&L" />
        </div>
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Decided win rate
          </div>
          <div className="font-mono text-xl tabular-nums text-foreground">
            {formatWinRatePct(pnl?.decided_trade_win_rate)}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {pnl?.win_count ?? 0} of {pnl?.decided_n ?? 0} decided trades (excludes {pnl?.breakeven_count ?? 0} breakeven)
          </div>
        </div>
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Expectancy</div>
          <div className={cn("font-mono text-xl tabular-nums", pnlToneClass(performance.expectancy_usd))}>
            {formatUsd(performance.expectancy_usd)}
          </div>
          <div className="text-[11px] text-muted-foreground">{performance.expectancy_note || "Mean P&L per trade"}</div>
        </div>
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Return on risk
          </div>
          <div className="font-mono text-xl tabular-nums text-foreground">
            {formatAlreadyPercent(returnOnRisk?.mean_return_on_risk_pct)}
            <span className="ml-1 text-[11px] font-normal text-muted-foreground">mean</span>
          </div>
          <div className="text-[11px] text-muted-foreground">
            median {formatAlreadyPercent(returnOnRisk?.median_return_on_risk_pct)} ·{" "}
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} role="note" className="cursor-help underline decoration-dotted underline-offset-2">
                  aggregate {formatAlreadyPercent(returnOnRisk?.aggregate_return_on_risk_pct)}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[320px]">
                {returnOnRisk?.aggregate_return_on_risk_note ||
                  "The aggregate ratio pools total P&L over total max risk across the population -- a differently-weighted number from the mean/median per-trade ratio above, never interchangeable with it."}
              </TooltipContent>
            </Tooltip>
          </div>
          {/* HIGH FIX 2: return-on-risk's own n/excluded, PLUS the bounded
              ledger-id exclusion sample -- inside a provenance affordance,
              since raw ledger ids are only allowed to render as content
              inside a labeled provenance panel (per the ticket). */}
          <div className="mt-1 text-[11px] text-muted-foreground">
            n = {returnOnRisk?.n ?? 0} priced trades, {returnOnRisk?.excluded.total_excluded ?? 0} excluded
            {returnOnRisk && returnOnRisk.excluded.total_excluded > 0 && (
              <>
                {" "}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      role="note"
                      className="cursor-help underline decoration-dotted underline-offset-2"
                    >
                      Provenance
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[360px]">
                    <dl className="space-y-1">
                      {Object.entries(returnOnRisk.excluded.by_reason).map(([reason, count]) => (
                        <div key={reason} className="flex items-baseline justify-between gap-3">
                          <dt className="text-muted-foreground">{reason}</dt>
                          <dd className="font-mono text-[10px]">{count}</dd>
                        </div>
                      ))}
                      {returnOnRisk.excluded.ledger_ids.length > 0 && (
                        <div>
                          <dt className="text-muted-foreground">
                            Excluded ledger ids ({returnOnRisk.excluded.disclosed_count} of{" "}
                            {returnOnRisk.excluded.total_excluded} shown)
                          </dt>
                          <dd className="mt-1 space-y-0.5 break-all font-mono text-[10px]">
                            {returnOnRisk.excluded.ledger_ids.map((id) => (
                              <div key={id}>{id}</div>
                            ))}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

// --------------------------------------------------------------------------- //
// Distribution buckets -- CSS-only bars, no charting library.
// --------------------------------------------------------------------------- //

function bucketToneClass(bucket: string): string {
  if (bucket.startsWith("LOSS")) return "bg-loss";
  if (bucket.startsWith("GAIN")) return "bg-gain";
  return "bg-muted-foreground/50";
}

function bucketLabel(bucket: string): string {
  return bucket
    .split("_")
    .map((part) => (part === "TO" ? "to" : part))
    .join(" ")
    .replace(/\bGT\b/g, ">")
    .replace(/\bLT\b/g, "<");
}

function DistributionBlock({ performance }: { performance: PerformanceAggregatesResponse }) {
  const distribution = performance.distribution;
  if (!distribution) {
    return (
      <SectionCard title="Outcome distribution">
        <p className="mt-2 text-sm text-muted-foreground">Distribution is unavailable for this read.</p>
      </SectionCard>
    );
  }
  const buckets: DistributionBucket[] = distribution.buckets;
  const maxCount = Math.max(1, ...buckets.map((bucket) => bucket.count));
  return (
    <SectionCard title="Outcome distribution">
      {buckets.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No resolved trades to bucket yet.</p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {buckets.map((bucket) => (
            <li key={bucket.bucket} className="flex items-center gap-2 text-[12px]">
              <span className="w-36 shrink-0 text-muted-foreground">{bucketLabel(bucket.bucket)}</span>
              <span className="h-3 flex-1 overflow-hidden rounded-sm bg-surface">
                <span
                  className={cn("block h-full rounded-sm", bucketToneClass(bucket.bucket))}
                  style={{ width: `${Math.max(4, (bucket.count / maxCount) * 100)}%` }}
                />
              </span>
              <span className="w-8 shrink-0 text-right font-mono tabular-nums text-foreground">{bucket.count}</span>
            </li>
          ))}
        </ul>
      )}
      <ExclusionCaption n={distribution.n} excluded={distribution.excluded} populationLabel="bucketed" />
    </SectionCard>
  );
}

// --------------------------------------------------------------------------- //
// Drawdown -- peak/trough as ledger-id-carrying links to the ledger table.
// --------------------------------------------------------------------------- //

function DrawdownPointLink({ point, label }: { point: DrawdownPoint | null | undefined; label: string }) {
  if (!point) {
    return (
      <div>
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
        <div className="text-sm text-muted-foreground">—</div>
      </div>
    );
  }
  // A drawdown point may be the synthetic zero-P&L baseline (label ===
  // "baseline", ledger_id === null) -- honest, not a broken link: there is
  // no real ledger document behind the baseline point, so it renders as
  // identified provenance text, not a link.
  const content = (
    <>
      <div className="font-mono text-sm tabular-nums text-foreground">
        {point.resolved_at ?? (point.label === "baseline" ? "start of record" : "—")}
      </div>
      <div className={cn("font-mono text-sm tabular-nums", pnlToneClass(point.cumulative_pnl_usd))}>
        {formatUsd(point.cumulative_pnl_usd)}
      </div>
    </>
  );
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      {point.ledger_id ? (
        // Round 2 FIX 5: `LEDGER_TARGET_PARAM` -- ONE ledger-targeting query
        // param, consumed by the ledger page via the backend's own
        // exact-id equality filter (never the row_id reconciliation
        // contract -- see tickerContextLink.ts's doc comment).
        <Link
          to={`/portal/trade-ideas/ledger?${LEDGER_TARGET_PARAM}=${encodeURIComponent(point.ledger_id)}`}
          className="block hover:underline"
        >
          {content}
        </Link>
      ) : (
        content
      )}
    </div>
  );
}

function DrawdownBlock({ performance }: { performance: PerformanceAggregatesResponse }) {
  const drawdown = performance.drawdown;
  if (!drawdown) {
    return (
      <SectionCard title="Drawdown">
        <p className="mt-2 text-sm text-muted-foreground">Drawdown is unavailable for this read.</p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Drawdown">
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Max drawdown
          </div>
          <div className="font-mono text-xl tabular-nums text-loss">{formatUsd(drawdown.max_drawdown_usd)}</div>
        </div>
        <DrawdownPointLink point={drawdown.peak_point} label="Peak" />
        <DrawdownPointLink point={drawdown.trough_point} label="Trough" />
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">{drawdown.ordering_convention}</p>
      <ExclusionCaption n={drawdown.n} excluded={drawdown.excluded} populationLabel="in the cumulative walk" />
    </SectionCard>
  );
}

// --------------------------------------------------------------------------- //
// Holding period
// --------------------------------------------------------------------------- //

function HoldingPeriodBlock({ performance }: { performance: PerformanceAggregatesResponse }) {
  const holdingPeriod = performance.holding_period;
  if (!holdingPeriod) {
    return (
      <SectionCard title="Holding period">
        <p className="mt-2 text-sm text-muted-foreground">Holding-period stats are unavailable for this read.</p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Holding period">
      <div className="mt-3 flex flex-wrap gap-8">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Mean days</div>
          <div className="font-mono text-xl tabular-nums text-foreground">{holdingPeriod.mean_days ?? "—"}</div>
        </div>
        <div>
          <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Median days
          </div>
          <div className="font-mono text-xl tabular-nums text-foreground">{holdingPeriod.median_days ?? "—"}</div>
        </div>
      </div>
      <ExclusionCaption n={holdingPeriod.n} excluded={holdingPeriod.excluded} populationLabel="with a computable holding period" />
    </SectionCard>
  );
}

// --------------------------------------------------------------------------- //
// Cohort cuts -- five sections, each row showing low_sample visibly.
// --------------------------------------------------------------------------- //

function cohortKeyLabel(cutName: string, key: string): string {
  if (key === "UNKNOWN" || key === "UNTAGGED") return key;
  if (cutName === "by_archetype") return archetypeLabel(key);
  if (cutName === "by_direction") return directionView(key).label;
  return key;
}

function CohortTable({ title, cutName, cohorts }: { title: string; cutName: string; cohorts: CohortAggregate[] }) {
  return (
    <SectionCard title={title}>
      {cohorts.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No resolved cohorts to show yet.</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-border text-left text-[9px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                <th className="py-1.5 pr-3">Cohort</th>
                <th className="py-1.5 pr-3">Resolved</th>
                <th className="py-1.5 pr-3">n</th>
                <th className="py-1.5 pr-3">Excluded</th>
                <th className="py-1.5 pr-3">Net P&amp;L</th>
                <th className="py-1.5 pr-3">Win rate</th>
                <th className="py-1.5 pr-3">Expectancy</th>
                <th className="py-1.5 pr-3">Return on risk (mean)</th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map((cohort) => {
                // Round 2 FIX 2: "same treatment in every cohort row" -- n
                // AND its exclusion count/reasons rendered inline, zero
                // excluded shown as "0" (never omitted). `pnl.excluded` is
                // this row's own reconciled exclusion source (matches
                // resolved_population - pnl.n, per outcome_ledger_reads'
                // _cohort_aggregate/_pnl_stats contract).
                const excludedTotal = cohort.pnl.excluded.total;
                const excludedReasons = Object.entries(cohort.pnl.excluded.by_reason);
                return (
                  <tr key={cohort.cohort_key} className="border-b border-border/60">
                    <td className="py-1.5 pr-3">
                      <span className="text-foreground">{cohortKeyLabel(cutName, cohort.cohort_key)}</span>
                      {cohort.low_sample && (
                        <Badge
                          variant="outline"
                          className="ml-2 border-warning/30 bg-warning/10 text-[9px] text-warning"
                        >
                          low sample
                        </Badge>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 font-mono tabular-nums text-foreground">
                      {cohort.resolved_population}
                    </td>
                    <td className="py-1.5 pr-3 font-mono tabular-nums text-foreground">{cohort.n}</td>
                    <td className="py-1.5 pr-3 font-mono tabular-nums text-muted-foreground">
                      {excludedReasons.length > 0 ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              tabIndex={0}
                              role="note"
                              className="cursor-help underline decoration-dotted underline-offset-2"
                            >
                              {excludedTotal}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[280px]">
                            <dl className="space-y-1">
                              {excludedReasons.map(([reason, count]) => (
                                <div key={reason} className="flex items-baseline justify-between gap-3">
                                  <dt className="text-muted-foreground">{reason}</dt>
                                  <dd className="font-mono text-[10px]">{count}</dd>
                                </div>
                              ))}
                            </dl>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        excludedTotal
                      )}
                    </td>
                    <td className={cn("py-1.5 pr-3 font-mono tabular-nums", pnlToneClass(cohort.pnl.total_pnl_usd))}>
                      {formatUsd(cohort.pnl.total_pnl_usd)}
                    </td>
                    <td className="py-1.5 pr-3 font-mono tabular-nums text-foreground">
                      {formatWinRatePct(cohort.pnl.decided_trade_win_rate)}
                    </td>
                    <td className={cn("py-1.5 pr-3 font-mono tabular-nums", pnlToneClass(cohort.expectancy_usd))}>
                      {formatUsd(cohort.expectancy_usd)}
                    </td>
                    <td className="py-1.5 pr-3 font-mono tabular-nums text-foreground">
                      {formatAlreadyPercent(cohort.return_on_risk.mean_return_on_risk_pct)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// --------------------------------------------------------------------------- //
// Transaction-cost disclosure -- render every string field verbatim.
// --------------------------------------------------------------------------- //

function TransactionCostDisclosureBlock({ performance }: { performance: PerformanceAggregatesResponse }) {
  const disclosure = performance.transaction_cost_disclosure;
  if (!disclosure || typeof disclosure !== "object") {
    return (
      <SectionCard title="Transaction-cost disclosure">
        <p className="mt-2 text-sm text-muted-foreground">Disclosure is unavailable for this read.</p>
      </SectionCard>
    );
  }
  const entries = Object.entries(disclosure as Record<string, unknown>).filter(
    ([, value]) => typeof value === "string",
  ) as Array<[string, string]>;
  return (
    <SectionCard title="Transaction-cost disclosure">
      <dl className="mt-3 space-y-3">
        {entries.map(([key, value]) => (
          <div key={key}>
            <dt className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {humanizeDisclosureKey(key)}
            </dt>
            <dd className="mt-0.5 text-[12px] text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  );
}

// --------------------------------------------------------------------------- //
// Scan telemetry -- de-emphasized footnote.
// --------------------------------------------------------------------------- //

function ScanTelemetryFootnote({ performance }: { performance: PerformanceAggregatesResponse }) {
  const scan = performance.scan;
  if (!scan) return null;
  return (
    <p className="text-[10px] text-muted-foreground">
      Scanned {scan.documents_scanned} document(s) in {scan.elapsed_ms}ms (cap {scan.max_scan_documents}).
    </p>
  );
}

export function PerformanceEmptyState() {
  return (
    <SectionCard title="Performance">
      <p className="mt-2 text-sm text-muted-foreground">
        No resolved ideas yet — the record accrues as published ideas resolve.
      </p>
    </SectionCard>
  );
}

export interface PerformancePageProps {
  performance: PerformanceAggregatesResponse;
}

/**
 * The aggregate performance content -- composed as a tab/section inside
 * LedgerPage.tsx (see the module comment above for why this isn't a separate
 * route).
 */
export function PerformancePage({ performance }: PerformancePageProps) {
  if (performance.resolved_count === 0) {
    return (
      <div className="space-y-4">
        <PopulationReconciliationTable performance={performance} />
        <PerformanceEmptyState />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <PopulationReconciliationTable performance={performance} />
      <HeadlineBlock performance={performance} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DistributionBlock performance={performance} />
        <DrawdownBlock performance={performance} />
      </div>
      <HoldingPeriodBlock performance={performance} />
      <CohortTable title="By archetype" cutName="by_archetype" cohorts={performance.by_archetype} />
      <CohortTable title="By direction" cutName="by_direction" cohorts={performance.by_direction} />
      <CohortTable title="By discovery lane" cutName="by_lane" cohorts={performance.by_lane} />
      <CohortTable title="By score band" cutName="by_score_band" cohorts={performance.by_score_band} />
      <CohortTable title="By publication month" cutName="by_publication_month" cohorts={performance.by_publication_month} />
      <TransactionCostDisclosureBlock performance={performance} />
      <ScanTelemetryFootnote performance={performance} />
    </div>
  );
}
